import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, catchError, EMPTY, from, map, Observable, of, tap } from 'rxjs';

import { environment } from 'src/environments/environment';

/**
 * Handles the Cognito PKCE OAuth flow and stores the resulting JWT for external mode.
 * A raw HttpClient (bypassing Angular interceptors) is used intentionally — interceptors
 * attach internal COLA session headers which would invalidate requests to the Cognito token endpoint.
 */
@Injectable({ providedIn: 'root' })
export class ExternalSessionService {
	// Initialized from localStorage so that external mode survives full page reloads
	private externalTokenSubject = new BehaviorSubject<string | null>(localStorage.getItem('externalSessionToken'));

	public externalToken$ = this.externalTokenSubject.asObservable();

	public isLoggedIn$ = this.externalToken$.pipe(map(token => !!token && !this.isTokenExpired(token)));

	constructor(
		private httpClient: HttpClient,
		private router: Router,
	) {}

	public isTokenExpired(token: string): boolean {
		try {
			const payload = JSON.parse(atob(token.split('.')[1]));
			return typeof payload['exp'] === 'number' && payload['exp'] * 1000 < Date.now();
		} catch {
			return true;
		}
	}

	public email$ = this.externalToken$.pipe(
		map(token => {
			if (!token) return null;
			try {
				const payload = JSON.parse(atob(token.split('.')[1]));
				return (payload['email'] ?? payload['cognito:username'] ?? null) as string | null;
			} catch {
				return null;
			}
		}),
	);

	public login$(): Observable<void> {
		const { domain, clientId, scopes } = environment.cognitoConfig;
		const verifier = this.generateCodeVerifier();

		return from(this.pkceChallenge(verifier)).pipe(
			map(challenge => {
				// sessionStorage rather than localStorage so the verifier is scoped to the current tab
				// and is automatically cleared when the tab closes.
				sessionStorage.setItem('pkce_verifier', verifier);

				window.location.href =
					`${domain}/oauth2/authorize` +
					`?client_id=${clientId}` +
					'&response_type=code' +
					`&scope=${encodeURIComponent(scopes.join(' '))}` +
					`&redirect_uri=${encodeURIComponent(environment.cognitoConfig.redirectUri)}` +
					'&code_challenge_method=S256' +
					`&code_challenge=${challenge}`;
			}),
		);
	}

	/**
	 * Exchanges the OAuth authorization code for tokens using PKCE. The verifier is stored
	 * in sessionStorage rather than localStorage so it is scoped to the current tab
	 * and automatically cleared when the browser tab closes.
	 * @param code
	 */
	public exchangeCodeForTokens$(code: string): Observable<string | null> {
		const { domain, clientId } = environment.cognitoConfig;
		const verifier = sessionStorage.getItem('pkce_verifier') ?? '';

		const body = new HttpParams({
			fromObject: { grant_type: 'authorization_code', client_id: clientId, code, redirect_uri: environment.cognitoConfig.redirectUri, code_verifier: verifier },
		}).toString();

		return this.httpClient.post<any>(`${domain}/oauth2/token`, body, { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) }).pipe(
			map(data => data?.id_token ?? null),
			tap(token => {
				if (token) {
					localStorage.setItem('externalSessionToken', token);
					this.externalTokenSubject.next(token);
					sessionStorage.removeItem('pkce_verifier');
				}
			}),
			catchError(err => {
				console.error('[ExternalSessionService] Token exchange failed:', err?.error ?? err?.message ?? err);
				return of(null);
			}),
		);
	}

	/**
	 * Clears the external session.
	 */
	public clearSession(): void {
		if (!this.externalTokenSubject.value) {
			return;
		}

		localStorage.removeItem('externalSessionToken');
		localStorage.setItem('isExternalMode', 'false');
		this.externalTokenSubject.next(null);
	}

	private generateCodeVerifier(): string {
		const a = new Uint8Array(32);
		crypto.getRandomValues(a);
		return [...a].map(b => b.toString(16).padStart(2, '0')).join('');
	}

	private pkceChallenge(v: string): Observable<string> {
		return from(crypto.subtle.digest('SHA-256', new TextEncoder().encode(v))).pipe(
			catchError(err => {
				console.error('pkceChallenge error:', err);
				return EMPTY;
			}),

			map(d =>
				btoa(String.fromCharCode(...new Uint8Array(d)))
					.replace(/\+/g, '-')
					.replace(/\//g, '_')
					.replace(/=+$/, ''),
			),
		);
	}
}
